import { useState } from 'react';
import { Avatar, DeleteButton, EmployeePicker, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import type { Comparison, EosMeasurable, EosScorecard as Scorecard, Unit } from './eosTypes';
import type { BusinessUnitOption } from './eosUi';
import {
  BusinessUnitPicker, formatMeasure, isHit, weekLabel } from './eosUi';

/** The comparison, written the way the original app wrote it. */
const GOAL_GLYPH: Record<Comparison, string> = { '>=': '≥', '<=': '≤', '=': '=' };

interface Editing {
  measurableId: number;
  week: string;
  value: string;
}

/**
 * The 13-week scorecard.
 *
 * The original app's table, closely: the measurable column frozen at the left,
 * the header row sticky, the goal written `≥ 10`, one column per week oldest to
 * newest, and the average on the end. Numbers right-aligned and tabular so a
 * column of them lines up.
 *
 * Cells are CLICK-TO-EDIT rather than permanent inputs — thirteen weeks by
 * seventeen measurables is 221 input boxes, and a wall of them hides the thing
 * the screen exists to show: which rows are missing their goal. Enter commits,
 * Escape cancels, blur commits.
 *
 * Rows drag to reorder, which is how a team gets their scorecard into the order
 * they read it out in.
 */
export function EosScorecard({
  teamId,
  onChanged,
  /** Off inside the meeting runner: Scorecard is a five-minute read-the-numbers
   *  section, and dragging rows around mid-meeting is not part of it. */
  reorderable = true,
}: {
  teamId: number;
  onChanged: () => void;
  reorderable?: boolean;
}) {
  const scorecard = useResource<Scorecard>(`/eos/scorecard?teamId=${teamId}&weeks=13`);
  const employees = useResource<Employee[]>('/employees?status=active');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [form, setForm] = useState<EosMeasurable | null>(null);
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const commit = async () => {
    if (!editing) return;
    const { measurableId, week, value } = editing;
    setEditing(null);
    const trimmed = value.trim();
    /* An empty cell CLEARS the score rather than storing a zero — "nobody has
       entered this yet" and "the number was nought" are different facts, and a
       hit rate counting the first as a miss is wrong. */
    if (trimmed !== '' && Number.isNaN(Number(trimmed))) {
      setError(`"${value}" is not a number.`);
      return;
    }
    setError(null);
    try {
      await api.put('/eos/scores', {
        measurableId,
        weekStart: week,
        value: trimmed === '' ? null : Number(trimmed),
      });
      scorecard.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that score.');
    }
  };

  /** Drop `dragId` onto `targetId` and persist the whole new order. */
  const reorder = async (targetId: number) => {
    setDragOverId(null);
    const list = scorecard.data?.measurables;
    if (dragId === null || dragId === targetId || !list) {
      setDragId(null);
      return;
    }
    const from = list.findIndex((m) => m.MeasurableId === dragId);
    const to = list.findIndex((m) => m.MeasurableId === targetId);
    setDragId(null);
    if (from < 0 || to < 0) return;

    const next = [...list];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);

    /* Optimistic, so the row lands where it was dropped rather than after a
       round trip — then every row's new position is written. Re-sequencing the
       whole list is what makes the order stable; patching only the moved row
       would leave two measurables sharing a SortOrder. */
    scorecard.set((current) => ({ ...current, measurables: next }));
    try {
      await Promise.all(
        next.map((m, index) => api.put(`/eos/measurables/${m.MeasurableId}`, { sortOrder: index })),
      );
      scorecard.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the new order.');
      scorecard.reload();
    }
  };

  if (scorecard.loading) return <Loading label="Loading the scorecard" />;
  if (scorecard.error) return <ErrorBanner message={scorecard.error} />;
  if (!scorecard.data) return null;

  const { weeks, measurables } = scorecard.data;
  const currentWeek = weeks[weeks.length - 1];

  return (
    <div className="eos">
      {error && <ErrorBanner message={error} />}

      <section className="card">
        <div className="card-head row-between wrap">
          <div>
            <h3>Scorecard</h3>
            <p className="xs subtle">
              Thirteen weeks against goal. Click a cell to enter a number; clear it to unset
              {reorderable && ', and drag a row to reorder'}.
            </p>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            Add measurable
          </button>
        </div>

        <div className="card-body">
          {measurables.length === 0 ? (
            <p className="muted">
              No measurables yet. Add one, or apply a scorecard template from the Templates tab.
            </p>
          ) : (
            <div className="eos-scorecard-wrap">
              <table className={`eos-scorecard${reorderable ? ' reorderable' : ''}`}>
                <thead>
                  <tr>
                    {reorderable && <th className="drag-col" aria-label="Reorder" />}
                    <th className="name-col">Measurable</th>
                    <th className="owner-col">Owner</th>
                    <th className="goal-col">Goal</th>
                    {weeks.map((week) => (
                      <th key={week}>{weekLabel(week)}</th>
                    ))}
                    <th className="avg-col">Avg</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {measurables.map((m) => {
                    const goal = Number(m.Goal);
                    const entered = weeks
                      .map((w) => m.scores[w])
                      .filter((v): v is number => v !== undefined && v !== null)
                      .map(Number);
                    const average =
                      entered.length > 0 ? entered.reduce((a, b) => a + b, 0) / entered.length : null;

                    return (
                      <tr
                        key={m.MeasurableId}
                        className={`${dragId === m.MeasurableId ? 'dragging' : ''} ${
                          dragOverId === m.MeasurableId ? 'drag-over' : ''
                        }`.trim()}
                        onDragOver={
                          reorderable
                            ? (e) => {
                                e.preventDefault();
                                if (dragOverId !== m.MeasurableId) setDragOverId(m.MeasurableId);
                              }
                            : undefined
                        }
                        onDrop={reorderable ? () => void reorder(m.MeasurableId) : undefined}
                      >
                        {reorderable && (
                          <td
                            className="drag-col"
                            draggable
                            onDragStart={() => setDragId(m.MeasurableId)}
                            onDragEnd={() => {
                              setDragId(null);
                              setDragOverId(null);
                            }}
                            title="Drag to reorder"
                            aria-label={`Reorder ${m.Name}`}
                          >
                            ⠿
                          </td>
                        )}

                        <td className="name-col">{m.Name}</td>
                        <td className="owner-col">
                          {/* An avatar, not a name: seventeen owner names would
                              double the width of a table that is already wide,
                              and the initials are enough to spot whose row it
                              is. The full name is on the edit form. */}
                          <Avatar name={m.OwnerName} />
                        </td>
                        <td className="goal-col">
                          {GOAL_GLYPH[m.Comparison]} {formatMeasure(goal, m.Unit)}
                        </td>

                        {weeks.map((week) => {
                          const raw = m.scores[week];
                          const value = raw === null || raw === undefined ? null : Number(raw);
                          const hit = isHit(value, goal, m.Comparison);
                          const isEditing =
                            editing?.measurableId === m.MeasurableId && editing.week === week;

                          return (
                            <td
                              key={week}
                              className={`score${hit === true ? ' hit' : hit === false ? ' miss' : ''}${
                                week === currentWeek ? ' current-week' : ''
                              }`}
                              onClick={() => {
                                if (!isEditing) {
                                  setEditing({
                                    measurableId: m.MeasurableId,
                                    week,
                                    value: value === null ? '' : String(value),
                                  });
                                }
                              }}
                            >
                              {isEditing ? (
                                <input
                                  /* eslint-disable-next-line jsx-a11y/no-autofocus */
                                  autoFocus
                                  type="text"
                                  inputMode="decimal"
                                  aria-label={`${m.Name}, week of ${weekLabel(week)}`}
                                  value={editing.value}
                                  onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                  onBlur={() => void commit()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') void commit();
                                    if (e.key === 'Escape') setEditing(null);
                                  }}
                                />
                              ) : (
                                formatMeasure(value, m.Unit)
                              )}
                            </td>
                          );
                        })}

                        <td className="avg-col">
                          {average === null
                            ? ''
                            : formatMeasure(
                                /* A count averages to a whole number; a rate
                                   keeps one decimal. */
                                m.Unit === '#' ? Math.round(average) : Math.round(average * 10) / 10,
                                m.Unit,
                              )}
                        </td>

                        <td>
                          <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setForm(m)}
                            >
                              Edit
                            </button>
                            <DeleteButton
                              label="Retire"
                              what={`the measurable "${m.Name}"`}
                              onConfirm={() => {
                                void api
                                  .del(`/eos/measurables/${m.MeasurableId}`)
                                  .then(() => {
                                    scorecard.reload();
                                    onChanged();
                                  })
                                  .catch((err: unknown) =>
                                    setError(
                                      err instanceof Error ? err.message : 'Could not retire it.',
                                    ),
                                  );
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {measurables.length > 0 && currentWeek && (
            <HitRate measurables={measurables} week={currentWeek} />
          )}
        </div>
      </section>

      {(adding || form) && (
        <MeasurableForm
          teamId={teamId}
          employees={employees.data ?? []}
          measurable={form}
          onClose={() => {
            setAdding(false);
            setForm(null);
          }}
          onSaved={() => {
            setAdding(false);
            setForm(null);
            scorecard.reload();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/**
 * This week's hit rate, under the grid.
 *
 * A fraction rather than a percentage, for the reason the CS module states
 * documented tools that way: "9 of 13" says how many rows to talk about in the
 * meeting, and "69%" does not. Measurables with no number yet are counted
 * separately rather than as misses — the week is not over.
 */
function HitRate({ measurables, week }: { measurables: EosMeasurable[]; week: string }) {
  let hit = 0;
  let scored = 0;
  for (const m of measurables) {
    const raw = m.scores[week];
    if (raw === null || raw === undefined) continue;
    scored++;
    if (isHit(Number(raw), Number(m.Goal), m.Comparison)) hit++;
  }
  const blank = measurables.length - scored;

  return (
    <div className="row wrap mt-4" style={{ gap: 12, alignItems: 'center' }}>
      <span className="xs subtle">Week of {weekLabel(week)}</span>
      <span
        className={`eos-pill ${scored === 0 ? 'neutral' : hit === scored ? 'good' : 'critical'}`}
      >
        {hit} of {scored} on goal
      </span>
      {blank > 0 && (
        <span className="xs subtle">
          {blank} {blank === 1 ? 'measurable has' : 'measurables have'} no number yet
        </span>
      )}
    </div>
  );
}

function MeasurableForm({
  teamId,
  employees,
  measurable,
  onClose,
  onSaved,
}: {
  teamId: number;
  employees: Employee[];
  measurable: EosMeasurable | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(measurable?.Name ?? '');
  const [ownerEmployeeId, setOwner] = useState<number | null>(measurable?.OwnerEmployeeId ?? null);
  const [unit, setUnit] = useState<Unit>(measurable?.Unit ?? '#');
  const [goal, setGoal] = useState(measurable ? String(Number(measurable.Goal)) : '0');
  const [comparison, setComparison] = useState<Comparison>(measurable?.Comparison ?? '>=');
  const [businessUnitId, setBusinessUnit] = useState<number | null>(
    measurable?.BusinessUnitId ?? null,
  );
  const units = useResource<BusinessUnitOption[]>('/eos/business-units');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) {
      setError('A measurable needs a name.');
      return;
    }
    const goalValue = Number(goal);
    if (Number.isNaN(goalValue)) {
      setError('The goal has to be a number.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), ownerEmployeeId, unit, goal: goalValue, comparison, businessUnitId };
      if (measurable) await api.put(`/eos/measurables/${measurable.MeasurableId}`, body);
      else await api.post('/eos/measurables', { ...body, teamId });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={measurable ? 'Edit measurable' : 'Add measurable'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Owner" hint="Who reports this number in the meeting.">
          <EmployeePicker employees={employees} value={ownerEmployeeId} onChange={setOwner} />
        </Field>
        <Field label="Unit">
          <select className="select" value={unit} onChange={(e) => setUnit(e.target.value as Unit)}>
            <option value="#">Number</option>
            <option value="$">Currency</option>
            <option value="%">Percentage</option>
          </select>
        </Field>
        <Field
          label="Business unit"
          hint="For the business review. Left unset it follows the owner's own unit."
        >
          <BusinessUnitPicker
            units={units.data ?? []}
            value={businessUnitId}
            onChange={setBusinessUnit}
          />
        </Field>
        <Field label="Goal rule" hint="Whether more, less or exactly the goal is the win.">
          <select
            className="select"
            value={comparison}
            onChange={(e) => setComparison(e.target.value as Comparison)}
          >
            <option value=">=">At least</option>
            <option value="<=">At most</option>
            <option value="=">Exactly</option>
          </select>
        </Field>
        <Field label="Goal">
          <input
            className="input tabular"
            type="text"
            inputMode="decimal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
